import { useState, useEffect, useCallback } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LangContext";
import axios from "axios";
import {
    LayoutDashboard, Users, Cpu, Car, CreditCard, Bell,
    Settings, Cog, MapPin, FileText, Menu, X, Search, LogOut,
    ChevronRight, Globe
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGO_URL = "/logo-white.svg";

const navItems = [
    { path: "/dashboard", icon: LayoutDashboard, key: "dashboard" },
    { path: "/clients", icon: Users, key: "clients" },
    { path: "/devices", icon: Cpu, key: "devices" },
    { path: "/vehicles", icon: Car, key: "vehicles" },
    { path: "/subscriptions", icon: MapPin, key: "subscriptions" },
    { path: "/payments", icon: CreditCard, key: "payments" },
    { path: "/alerts", icon: Bell, key: "alerts" },
];

export default function Layout() {
    const { user, logout } = useAuth();
    const { t, lang, toggleLang } = useLang();
    const location = useLocation();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState(null);
    const [searchLoading, setSearchLoading] = useState(false);
    const [alertCount, setAlertCount] = useState(0);

    useEffect(() => {
        fetchAlertCount();
    }, []);

    const fetchAlertCount = async () => {
        try {
            const { data } = await axios.get(`${API}/alerts`, { withCredentials: true });
            const total = (data.counts?.expiring || 0) + (data.counts?.expired || 0) + (data.counts?.debtors || 0);
            setAlertCount(total);
        } catch (e) {}
    };

    useEffect(() => {
        const handler = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === "k") {
                e.preventDefault();
                setSearchOpen(true);
            }
            if (e.key === "Escape") {
                setSearchOpen(false);
                setSearchQuery("");
                setSearchResults(null);
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, []);

    const doSearch = useCallback(async (q) => {
        if (!q || q.length < 2) { setSearchResults(null); return; }
        setSearchLoading(true);
        try {
            const { data } = await axios.get(`${API}/search`, { params: { q }, withCredentials: true });
            setSearchResults(data);
        } catch (e) {}
        setSearchLoading(false);
    }, []);

    useEffect(() => {
        const t = setTimeout(() => doSearch(searchQuery), 300);
        return () => clearTimeout(t);
    }, [searchQuery, doSearch]);

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    const Sidebar = ({ mobile = false }) => (
        <aside className={`${mobile ? "w-full" : "w-64 fixed left-0 top-0 min-h-screen"} bg-black flex flex-col z-20`}>
            <div className="px-5 py-6 border-b border-white/10">
                <div className="flex items-center justify-center">
                    <img
                        src={LOGO_URL}
                        alt="Illyria GPS"
                        className="h-14 w-auto object-contain"
                        onError={e => { e.target.style.display = "none"; }}
                    />
                </div>
            </div>

            <nav className="flex-1 px-3 py-3 space-y-0.5 sidebar-scroll overflow-y-auto">
                {navItems.map(item => {
                    const active = location.pathname.startsWith(item.path);
                    return (
                        <Link
                            key={item.path}
                            to={item.path}
                            onClick={() => setSidebarOpen(false)}
                            data-testid={`nav-${item.key}`}
                            className={`flex items-center gap-3 px-3 py-2.5 rounded text-[15px] font-medium transition-colors duration-150 ${
                                active
                                    ? "bg-[#E7212E]/20 text-[#E7212E] border-l-2 border-[#E7212E] -ml-px pl-[calc(0.75rem+1px)]"
                                    : "text-white hover:bg-white/5"
                            }`}
                        >
                            <item.icon size={16} />
                            <span>{t(item.key)}</span>
                            {item.key === "alerts" && alertCount > 0 && (
                                <span className="ml-auto bg-[#E7212E] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                                    {alertCount > 9 ? "9+" : alertCount}
                                </span>
                            )}
                        </Link>
                    );
                })}
                {user?.role === "admin" && (
                    <Link
                        to="/users"
                        onClick={() => setSidebarOpen(false)}
                        data-testid="nav-users"
                        className={`flex items-center gap-3 px-3 py-2.5 rounded text-[15px] font-medium transition-colors duration-150 ${
                            location.pathname === "/users"
                                ? "bg-[#E7212E]/20 text-[#E7212E] border-l-2 border-[#E7212E] -ml-px pl-[calc(0.75rem+1px)]"
                                : "text-white hover:bg-white/5"
                        }`}
                    >
                        <Settings size={16} />
                        <span>{t("users")}</span>
                    </Link>
                )}
                {user?.role === "admin" && (
                    <Link
                        to="/settings"
                        onClick={() => setSidebarOpen(false)}
                        data-testid="nav-settings"
                        className={`flex items-center gap-3 px-3 py-2.5 rounded text-[15px] font-medium transition-colors duration-150 ${
                            location.pathname === "/settings"
                                ? "bg-[#E7212E]/20 text-[#E7212E] border-l-2 border-[#E7212E] -ml-px pl-[calc(0.75rem+1px)]"
                                : "text-white hover:bg-white/5"
                        }`}
                    >
                        <Cog size={16} />
                        <span>Konfigurime</span>
                    </Link>
                )}
            </nav>
        </aside>
    );

    return (
        <div className="flex h-screen bg-slate-50">
            {/* Desktop Sidebar */}
            <div className="hidden md:block w-64 flex-shrink-0">
                <Sidebar />
            </div>

            {/* Mobile Sidebar */}
            {sidebarOpen && (
                <div className="fixed inset-0 z-30 md:hidden">
                    <div className="absolute inset-0 bg-black/60" onClick={() => setSidebarOpen(false)} />
                    <div className="absolute left-0 top-0 w-72 h-full">
                        <Sidebar mobile />
                    </div>
                </div>
            )}

            {/* Main */}
            <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
                {/* Header */}
                <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3 flex-shrink-0">
                    <button
                        onClick={() => setSidebarOpen(true)}
                        className="md:hidden p-1.5 rounded text-slate-500 hover:bg-slate-100"
                    >
                        <Menu size={18} />
                    </button>

                    <button
                        onClick={() => setSearchOpen(true)}
                        data-testid="global-search-btn"
                        className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded text-sm text-slate-500 transition-colors flex-1 max-w-sm"
                    >
                        <Search size={14} />
                        <span>{t("search_placeholder")}</span>
                        <span className="ml-auto text-xs text-slate-400 hidden sm:block">Ctrl+K</span>
                    </button>

                    <div className="ml-auto flex items-center gap-2">
                        <button
                            onClick={toggleLang}
                            data-testid="language-toggle-btn"
                            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs font-medium text-slate-600 hover:bg-slate-100 transition-colors border border-slate-200"
                        >
                            <Globe size={13} />
                            {lang === "sq" ? "SQ" : "EN"}
                        </button>
                        <Link
                            to="/alerts"
                            className="relative p-1.5 rounded text-slate-500 hover:bg-slate-100"
                            data-testid="alerts-header-btn"
                        >
                            <Bell size={17} />
                            {alertCount > 0 && (
                                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#E7212E] text-white text-xs rounded-full flex items-center justify-center font-bold">
                                    {alertCount > 9 ? "9+" : alertCount}
                                </span>
                            )}
                        </Link>
                        <div className="w-px h-6 bg-slate-200 mx-1" />
                        <div className="flex items-center gap-2.5 pr-1" data-testid="header-user-info">
                            <div className="text-right min-w-0 hidden sm:block">
                                <p className="text-xs font-semibold text-slate-900 truncate leading-tight">{user?.name}</p>
                                <p className="text-[10px] text-slate-500 capitalize leading-tight">{user?.role}</p>
                            </div>
                            <div className="w-8 h-8 rounded-full bg-[#E7212E] text-white flex items-center justify-center text-xs font-bold uppercase">
                                {user?.name?.charAt(0) || "?"}
                            </div>
                            <button
                                onClick={handleLogout}
                                data-testid="logout-btn"
                                title={t("logout")}
                                className="p-1.5 rounded text-slate-500 hover:bg-slate-100 hover:text-[#E7212E] transition-colors"
                            >
                                <LogOut size={15} />
                            </button>
                        </div>
                    </div>
                </header>

                {/* Content */}
                <main className="flex-1 overflow-auto">
                    <div className="animate-fade-in">
                        <Outlet />
                    </div>
                </main>
            </div>

            {/* Search Modal */}
            {searchOpen && (
                <div className="fixed inset-0 z-50 flex items-start justify-center pt-16 px-4">
                    <div className="absolute inset-0 bg-black/40" onClick={() => { setSearchOpen(false); setSearchQuery(""); setSearchResults(null); }} />
                    <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl border border-slate-200">
                        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200">
                            <Search size={16} className="text-slate-400 flex-shrink-0" />
                            <input
                                autoFocus
                                type="text"
                                placeholder={t("search_placeholder")}
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                data-testid="search-modal-input"
                                className="flex-1 outline-none text-sm text-slate-900 placeholder-slate-400"
                            />
                            <button onClick={() => { setSearchOpen(false); setSearchQuery(""); setSearchResults(null); }}>
                                <X size={16} className="text-slate-400" />
                            </button>
                        </div>

                        <div className="max-h-96 overflow-y-auto">
                            {searchLoading && (
                                <div className="px-4 py-6 text-center text-sm text-slate-400">{t("loading")}</div>
                            )}
                            {searchResults && !searchLoading && (
                                <>
                                    {searchResults.clients.length === 0 && searchResults.devices.length === 0 && searchResults.vehicles.length === 0 && (
                                        <div className="px-4 py-8 text-center text-sm text-slate-400">{t("no_results")}</div>
                                    )}
                                    {searchResults.clients.length > 0 && (
                                        <div>
                                            <p className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-400 bg-slate-50">{t("results_clients")}</p>
                                            {searchResults.clients.map(c => (
                                                <button
                                                    key={c.id}
                                                    onClick={() => { navigate("/clients"); setSearchOpen(false); setSearchQuery(""); setSearchResults(null); }}
                                                    className="w-full px-4 py-3 text-left hover:bg-slate-50 flex items-center justify-between border-b border-slate-100"
                                                >
                                                    <div>
                                                        <p className="text-sm font-medium text-slate-900">{c.name}</p>
                                                        <p className="text-xs text-slate-500">{c.nipt} · {c.phone}</p>
                                                    </div>
                                                    <ChevronRight size={14} className="text-slate-400" />
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    {searchResults.devices.length > 0 && (
                                        <div>
                                            <p className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-400 bg-slate-50">{t("results_devices")}</p>
                                            {searchResults.devices.map(d => (
                                                <button
                                                    key={d.id}
                                                    onClick={() => { navigate("/devices"); setSearchOpen(false); setSearchQuery(""); setSearchResults(null); }}
                                                    className="w-full px-4 py-3 text-left hover:bg-slate-50 flex items-center justify-between border-b border-slate-100"
                                                >
                                                    <div>
                                                        <p className="text-sm font-mono font-medium text-slate-900">{d.imei}</p>
                                                        <p className="text-xs text-slate-500">{d.model} · {d.client_name || t("no_client")}</p>
                                                    </div>
                                                    <ChevronRight size={14} className="text-slate-400" />
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    {searchResults.vehicles.length > 0 && (
                                        <div>
                                            <p className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-400 bg-slate-50">{t("results_vehicles")}</p>
                                            {searchResults.vehicles.map(v => (
                                                <button
                                                    key={v.id}
                                                    onClick={() => { navigate("/vehicles"); setSearchOpen(false); setSearchQuery(""); setSearchResults(null); }}
                                                    className="w-full px-4 py-3 text-left hover:bg-slate-50 flex items-center justify-between border-b border-slate-100"
                                                >
                                                    <div>
                                                        <p className="text-sm font-mono font-medium text-slate-900">{v.plate}</p>
                                                        <p className="text-xs text-slate-500">{v.model} · {v.driver || "-"}</p>
                                                    </div>
                                                    <ChevronRight size={14} className="text-slate-400" />
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                            {!searchQuery && !searchResults && (
                                <div className="px-4 py-8 text-center text-sm text-slate-400">
                                    Shkruani të paktën 2 karaktere për të kërkuar...
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
